import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AppSecurityRight, AuthenticationRoleResponse, EnvironmentConfiguration, TenantInfoRequest, UserType } from '@jms/model';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { UtilService } from '@jms/utils/util.services';
import { firstValueFrom } from 'rxjs';
import { ApplicationActions } from '@jms/tenant/module.jms.state';
import { ContextService } from '@jms/ui/ui.standalone.services/context.service';
import { AuthTokenService, PORTAL_AUTH_TOKEN_KEY } from '@jms/utils/util.authentication';
import { SharedSocketService } from '@workbuddy/services';
// logged-in.guard.ts

@Injectable()
export class AuthGuard {
  private token: string;
  private tenantInfo: TenantInfoRequest;
  private environment: EnvironmentConfiguration;

  constructor(
    private router: Router,
    private http: HttpClient,
    private location: Location,
    private context: ContextService,
    private actions: ApplicationActions,
    private jwt: JwtHelperService,
    private sharedSocket: SharedSocketService,
    private authTokenService: AuthTokenService
  ) {
    actions.token$.subscribe(x => (this.token = x));
    actions.tenantInfo$.subscribe(x => (this.tenantInfo = x));
    this.environment = ConfigService.environment;
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {

    const url = this.location.path();

    if (!url.toLowerCase().startsWith('/callback')) {
      const token = UtilService.getURLParameter('token', url);
      if (token) {
        let stateUrl = state.url;
        const index = stateUrl.indexOf('?');
        if (index >= 0) {
          stateUrl = stateUrl.substring(0, index);
        }
        const queryParams = {
          queryParams: {
            token: token,
            returnUrl: stateUrl
          }
        };
        this.router.navigate([`/callback`], queryParams);
        return false;
      }
    }

    if (this.token && !this.jwt.isTokenExpired(this.token)) {
      const portalRedirect = this.getPortalRedirect(this.token);

      if (portalRedirect === 'deny') {
        return this.accessDenied();
      }

      if (portalRedirect) {
        this.redirectToPortal(portalRedirect, this.token);
        return false;
      }

      if (!this.tenantInfo) {
        this.actions.getTenantInfo(state.url, true);
        return false;
      }

      return true;
    }

    if (this.token) {
      try {
        const refresh = await firstValueFrom(this.http.post<AuthenticationRoleResponse>('/api/authentication/refresh', { token: this.token }));

        if (refresh?.success && refresh.token) {
          this.actions.loginAuthorised(refresh.token, undefined, refresh.roleToken, state.url, true);
          this.sharedSocket.reinitialize();

          const portalRedirect = this.getPortalRedirect(refresh.token);

          if (portalRedirect === 'deny') {
            return this.accessDenied();
          }

          if (portalRedirect) {
            this.redirectToPortal(portalRedirect, refresh.token);
            return false;
          }

          if (!this.tenantInfo) {
            this.actions.getTenantInfo(state.url, true);
            return false;
          }

          return true;
        }
      } catch (error) {

      }
    }

    const callback = `${this.context.origin}${state.url}`;
    this.authTokenService.clear();
    // Redirect the user before denying them access to this route, carrying the tenant's SSO hint.
    UtilService.navigate(this.environment.jms.logoutHref, callback, UtilService.ssoHintParams(this.environment.sso));
    return false;
  }

  private getPortalRedirect(token: string): '/clients/' | '/contractors/' | '/portal-select' | 'deny' | null {

    const user = this.jwt.decodeToken(token) as { role?: { type?: UserType; rights?: number[] }; tenants?: Array<{ role?: { type?: UserType; rights?: number[] }; tenantUrl?: string; url?: string; tenant?: { url?: string } }> };
    const tenant = this.getCurrentTenant(user?.tenants ?? []);
    const role = tenant?.role ?? user?.role;

    if (role?.type !== UserType.LoginContact) return null;

    // Portal login contacts may only enter when the tenant has the enterprise portal provisioned;
    // otherwise deny — they have no place in the main tenant app.
    if (this.environment?.enterprisePortal !== true) return 'deny';

    const rights = role?.rights ?? [];
    const canAccessClientPortal = rights.includes(AppSecurityRight.LoginClient);
    const canAccessContractorPortal = rights.includes(AppSecurityRight.LoginContractor);

    switch (true) {
      case canAccessClientPortal && canAccessContractorPortal:
        return '/portal-select';

      case canAccessClientPortal:
        return '/clients/';

      case canAccessContractorPortal:
        return '/contractors/';

      default:
        return null;
    }
  }

  private redirectToPortal(path: '/clients/' | '/contractors/' | '/portal-select', token: string): void {

    if (path === '/portal-select') {
      this.router.navigate([path]);
      return;
    }

    // Path-mode portals share this origin's localStorage but read their session from
    // portal_auth_token, so seed it before handing over. The token already carries the
    // matching portal login right — getPortalRedirect only selects a portal path for it.
    localStorage.setItem(PORTAL_AUTH_TOKEN_KEY, token);
    window.location.assign(`${this.context.origin}${path}`);
  }

  /** Deny access and send the user to the access-denied page. Always returns false to block the guarded route. */
  private accessDenied(): boolean {

    this.router.navigate(['/login/access-denied']);
    return false;
  }

  private getCurrentTenant(tenants: Array<{ tenantUrl?: string; url?: string; tenant?: { url?: string } }>): { role?: { type?: UserType; rights?: number[] } } | null {

    const currentHost = this.context.web.toLowerCase();
    return tenants.find(tenant => this.getTenantUrl(tenant) === currentHost) ?? (tenants.length === 1 ? tenants[0] as any : null);
  }

  private getTenantUrl(tenant: { tenantUrl?: string; url?: string; tenant?: { url?: string } }): string {

    return (tenant?.tenantUrl ?? tenant?.url ?? tenant?.tenant?.url ?? '').toLowerCase();
  }
}
